'use client';

import { useEffect, useRef } from 'react';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import {
  closeAuthGate,
  openAuthGate,
  trackAuthGateCompleted,
  TRIGGER_BY_PENDING_ACTION,
  useAuthGateStore,
} from '@/features/auth/shared/state/auth-gate.store';
import { claimPendingGateAction } from '@/features/auth/shared/state/pending-gate-action';
import { runPendingGateAction } from '@/features/auth/shared/helpers/run-pending-gate-action';

/** How long the "you're all set" view stays up before the action takes over. */
const SUCCESS_VIEW_MS = 1500;

export const AuthGateResumer = () => {
  const { loading, authenticatedUser } = useUser();
  const resumed = useRef(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  useEffect(() => {
    if (loading || !authenticatedUser || resumed.current) return;
    if (useAuthGateStore.getState().pendingAction) return;

    const claim = claimPendingGateAction();
    if (claim.status !== 'claimed') return;

    const { action, method } = claim;
    resumed.current = true;
    trackAuthGateCompleted({
      trigger: TRIGGER_BY_PENDING_ACTION[action.type],
      resumed: true,
      method,
      outcome: 'completed',
    });
    openAuthGate({ view: 'success' });

    timer.current = window.setTimeout(() => {
      timer.current = null;
      runPendingGateAction(action);
      closeAuthGate();
    }, SUCCESS_VIEW_MS);
  }, [authenticatedUser, loading]);

  return null;
};
